import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";
import { useMockAuth, shouldUseMockAuth } from "../../auth/mockAuth";
import { useE2EAuth, isE2ETestMode } from "../../auth/e2eAuth";

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const navigate = useNavigate();

  // Priority: E2E auth > Mock auth > Real auth
  const realAuth = useAuth();
  const mockAuth = useMockAuth();
  const e2eAuth = useE2EAuth();

  // Choose authentication method based on context
  const auth = isE2ETestMode()
    ? e2eAuth
    : shouldUseMockAuth()
      ? mockAuth
      : realAuth;
  const { isAuthenticated, inProgress, login } = auth;

  useEffect(() => {
    if (!isAuthenticated && inProgress === "none") {
      // Preserve the current path so the user lands back here after the login redirect.
      // App.tsx clears auth.returnUrl on non-redirect page loads, so this runs after
      // that cleanup and is preserved through the Azure AD redirect cycle.
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath && currentPath !== "/") {
        localStorage.setItem("auth.returnUrl", currentPath);
      }
      login().catch((error) => {
        console.error("Authentication failed:", error);
      });
    }
  }, [isAuthenticated, inProgress, login]);

  useEffect(() => {
    if (isAuthenticated && inProgress === "none") {
      const returnUrl = localStorage.getItem("auth.returnUrl");
      if (returnUrl) {
        // Consume the stored returnUrl once authenticated. Removing it even when it
        // already matches the current path is important: otherwise a deep-linked load
        // (e.g. /terminal/lot-identification) leaves a stale returnUrl that yanks the
        // user back here on their first in-app navigation. Navigate only when it differs.
        localStorage.removeItem("auth.returnUrl");
        if (returnUrl !== window.location.pathname) {
          navigate(returnUrl);
        }
      }
    }
  }, [isAuthenticated, inProgress, navigate]);

  // Show loading while authentication is in progress
  if (inProgress !== "none") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-graphite-surface-2">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600 dark:text-graphite-muted">Přihlašování...</p>
        </div>
      </div>
    );
  }

  // Show loading while waiting for authentication check
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-graphite-surface-2">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600 dark:text-graphite-muted">Kontrola přihlášení...</p>
        </div>
      </div>
    );
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
};

export default AuthGuard;
